import { useEffect, useRef, useState } from 'react';
import SharedBuffySprite from '../buffy/BuffySprite';
import { useStill } from './useReveal';

/* Buffy for the sections below the hero, on the hero's shared, proven player
   (buffy/BuffySprite.jsx: one pre-decoded atlas, step-end transform keyframes,
   so a clip change can never flash a blank frame).

   Plays `sequence` as: gesture, idle breathing for `idleMs`, next gesture,
   idle, ... and loops. Every gesture starts and ends on the rest pose and
   idle-breathe starts on rest, so the chain never cuts. Pauses while off
   screen; reduced motion shows the still rest pose.

   Callers pass size (128 or 192 = 2x or 3x the 64px cell), className, style,
   and optionally sequence / idleMs. */
export default function BuffySprite({ size = 192, className = '', style, sequence = ['wave'], idleMs = 4800 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const reduced = useStill();

  const steps = sequence.flatMap((clip) => [clip, 'idle-breathe']);
  const clip = steps[step % steps.length];
  const idle = clip === 'idle-breathe';
  const next = () => setStep((n) => n + 1);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const io = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Idle is a loop, so it ends on a timer (it can leave on any breath frame:
  // every idle frame is adjacent to rest, the first frame of each gesture).
  useEffect(() => {
    if (!idle || !visible || reduced) return undefined;
    const t = setTimeout(next, idleMs);
    return () => clearTimeout(t);
  }, [idle, visible, reduced, idleMs, step]);

  return (
    <span ref={ref} className={`cxs-buffy ${className}`} style={{ width: size, height: size, ...style }} aria-hidden="true">
      <SharedBuffySprite
        clip={reduced ? 'rest' : clip}
        scale={Math.round(size / 64)}
        paused={!visible || reduced}
        loop={reduced ? false : idle}
        run={step}
        onEnd={idle || reduced ? undefined : next}
      />
    </span>
  );
}
